import React, { useState } from 'react';
import { superAdminApi } from '../../api/client';
import { FaSchool, FaUser, FaPalette } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

export default function RegisterSchool() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '', slug: '', address: '', phone: '', school_email: '',
    website: '', primary_color: '#0d6efd', secondary_color: '#6c757d',
    motto: '', principal_name: '',
    admin_name: '', admin_email: '',
  });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => {
    const upd = { ...form, [k]: v };
    // Auto-generate slug from name
    if (k === 'name') {
      upd.slug = v.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').trim('-');
    }
    setForm(upd);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await superAdminApi.createSchool(form);
      toast.success('School registered! Login credentials emailed to the admin.');
      navigate('/super-admin/schools');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to register school');
    } finally { setLoading(false); }
  };

  const field = (label, key, type = 'text', required = false, placeholder = '') => (
    <div style={{ marginBottom: 16 }}>
      <label className="sms-label">{label}{required && <span style={{ color: '#ef4444' }}>*</span>}</label>
      <input className="sms-input" type={type} value={form[key]} placeholder={placeholder}
        onChange={e => set(key, e.target.value)} required={required} />
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <h2>Register New School</h2>
        <p>Add a new school to the platform</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>

          {/* School Info */}
          <div className="sms-card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, color: '#0d6efd' }}>
              <FaSchool /> <h5 style={{ margin: 0, fontWeight: 600 }}>School Information</h5>
            </div>
            {field('School Name', 'name', 'text', true, 'e.g. Greenwood High School')}
            <div style={{ marginBottom: 16 }}>
              <label className="sms-label">School URL Slug</label>
              <div style={{ display: 'flex', alignItems: 'center', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8, padding: '9px 14px', gap: 6 }}>
                <span style={{ color: '#9ca3af', fontSize: '0.88rem' }}>localhost:3001/school/</span>
                <span style={{ fontWeight: 700, color: '#0d6efd', fontSize: '0.88rem' }}>{form.slug || <span style={{ color: '#d1d5db' }}>your-slug</span>}</span>
                <span style={{ color: '#9ca3af', fontSize: '0.88rem' }}>/login</span>
              </div>
              <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: 4, display: 'block' }}>Auto-generated from school name</small>
            </div>
            {field('Address', 'address', 'text', false, '123 School Road')}
            {field('Phone Number', 'phone', 'tel', false, '+27 11 000 0000')}
            {field('School Email', 'school_email', 'email', false, 'info@school.com')}
            {field('Website', 'website', 'url', false, 'https://school.com')}
            {field('School Motto', 'motto', 'text', false, 'e.g. Excellence in Education')}
            {field("Principal's Name", 'principal_name', 'text', false, 'Mr. / Mrs. ...')}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* Branding */}
            <div className="sms-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, color: '#8b5cf6' }}>
                <FaPalette /> <h5 style={{ margin: 0, fontWeight: 600 }}>School Branding</h5>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label className="sms-label">Primary Color</label>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <input type="color" value={form.primary_color} onChange={e => set('primary_color', e.target.value)}
                      style={{ width: 48, height: 38, border: 'none', borderRadius: 8, cursor: 'pointer', padding: 2 }} />
                    <input className="sms-input" value={form.primary_color} onChange={e => set('primary_color', e.target.value)} style={{ flex: 1 }} />
                  </div>
                </div>
                <div>
                  <label className="sms-label">Secondary Color</label>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <input type="color" value={form.secondary_color} onChange={e => set('secondary_color', e.target.value)}
                      style={{ width: 48, height: 38, border: 'none', borderRadius: 8, cursor: 'pointer', padding: 2 }} />
                    <input className="sms-input" value={form.secondary_color} onChange={e => set('secondary_color', e.target.value)} style={{ flex: 1 }} />
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div style={{ marginTop: 16, background: form.primary_color, borderRadius: 10, padding: '14px 18px', color: '#fff' }}>
                <div style={{ fontWeight: 700 }}>{form.name || 'School Name Preview'}</div>
                <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>{form.motto || 'School Motto'}</div>
              </div>
            </div>

            {/* Admin Account */}
            <div className="sms-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, color: '#10b981' }}>
                <FaUser /> <h5 style={{ margin: 0, fontWeight: 600 }}>Admin Account</h5>
              </div>
              {field('Admin Full Name', 'admin_name', 'text', true, 'John Smith')}
              {field('Admin Email', 'admin_email', 'email', true, 'admin@school.com')}
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                <span style={{ fontSize: '1.2rem' }}>🔐</span>
                <div>
                  <div style={{ fontWeight: 600, color: '#065f46', fontSize: '0.88rem' }}>Password auto-generated</div>
                  <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>A secure password will be generated and emailed to the admin</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
          <button type="submit" disabled={loading} className="sms-btn sms-btn-primary" style={{ padding: '12px 32px' }}>
            {loading ? 'Registering...' : 'Register School'}
          </button>
          <button type="button" onClick={() => navigate('/super-admin/schools')} className="sms-btn sms-btn-outline">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
